// src/i18n/config.ts
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import Backend from 'i18next-http-backend';
import { format } from 'date-fns';
import { enUS, es, fr, de, zhCN, arSA, ru } from 'date-fns/locale';

// Date locales mapping
export const dateLocales: Record<string, Locale> = {
  en: enUS,
  es: es,
  fr: fr,
  de: de,
  zh: zhCN,
  ar: arSA,
  ru: ru
};

// Supported languages
export const languages = [
  { code: 'en', name: 'English', nativeName: 'English', flag: '🇬🇧', dir: 'ltr' },
  { code: 'fr', name: 'French', nativeName: 'Français', flag: '🇫🇷', dir: 'ltr' },
  { code: 'ar', name: 'Arabic', nativeName: 'العربية', flag: '🇸🇦', dir: 'rtl' },
  // ─── African languages ───
  { code: 'yo', name: 'Yoruba', nativeName: 'Yorùbá', flag: '🇳🇬', dir: 'ltr' },
  { code: 'ha', name: 'Hausa', nativeName: 'Hausa', flag: '🇳🇬', dir: 'ltr' },
  { code: 'sw', name: 'Swahili', nativeName: 'Kiswahili', flag: '🇰🇪', dir: 'ltr' },
  { code: 'am', name: 'Amharic', nativeName: 'አማርኛ', flag: '🇪🇹', dir: 'ltr' },
  // ─── Asian languages ───
  { code: 'hi', name: 'Hindi', nativeName: 'हिन्दी', flag: '🇮🇳', dir: 'ltr' },
  { code: 'ur', name: 'Urdu', nativeName: 'اردو', flag: '🇵🇰', dir: 'rtl' },
  { code: 'bn', name: 'Bengali', nativeName: 'বাংলা', flag: '🇧🇩', dir: 'ltr' },
  { code: 'tl', name: 'Filipino', nativeName: 'Filipino', flag: '🇵🇭', dir: 'ltr' },
  // ─── Other majors ───
  { code: 'es', name: 'Spanish', nativeName: 'Español', flag: '🇪🇸', dir: 'ltr' },
  { code: 'de', name: 'German', nativeName: 'Deutsch', flag: '🇩🇪', dir: 'ltr' },
  { code: 'zh', name: 'Chinese', nativeName: '中文', flag: '🇨🇳', dir: 'ltr' },
  { code: 'ru', name: 'Russian', nativeName: 'Русский', flag: '🇷🇺', dir: 'ltr' }
];

// Currency formats
export const currencyFormats: Record<string, Intl.NumberFormat> = {
  en: new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }),
  es: new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' }),
  fr: new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }),
  de: new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }),
  zh: new Intl.NumberFormat('zh-CN', { style: 'currency', currency: 'CNY' }),
  ar: new Intl.NumberFormat('ar-SA', { style: 'currency', currency: 'SAR' }),
  ru: new Intl.NumberFormat('ru-RU', { style: 'currency', currency: 'RUB' })
};

// Number formats
export const numberFormats: Record<string, Intl.NumberFormat> = {
  en: new Intl.NumberFormat('en-US'),
  es: new Intl.NumberFormat('es-ES'),
  fr: new Intl.NumberFormat('fr-FR'),
  de: new Intl.NumberFormat('de-DE'),
  zh: new Intl.NumberFormat('zh-CN'),
  ar: new Intl.NumberFormat('ar-SA'),
  ru: new Intl.NumberFormat('ru-RU')
};

// Date formats
export const dateFormats: Record<string, Intl.DateTimeFormat> = {
  en: new Intl.DateTimeFormat('en-US', { dateStyle: 'medium' }),
  es: new Intl.DateTimeFormat('es-ES', { dateStyle: 'medium' }),
  fr: new Intl.DateTimeFormat('fr-FR', { dateStyle: 'medium' }),
  de: new Intl.DateTimeFormat('de-DE', { dateStyle: 'medium' }),
  zh: new Intl.DateTimeFormat('zh-CN', { dateStyle: 'medium' }),
  ar: new Intl.DateTimeFormat('ar-SA', { dateStyle: 'medium' }),
  ru: new Intl.DateTimeFormat('ru-RU', { dateStyle: 'medium' })
};

i18n
  .use(Backend)
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    fallbackLng: 'en',
    ns: ['common'],
    defaultNS: 'common',
    fallbackNS: 'common',
    debug: process.env.NODE_ENV === 'development',
    
    interpolation: {
      escapeValue: false,
      format: (value, format, lng) => {
        const L = lng || 'en';
        if (format === 'currency') {
          return (currencyFormats[L] || currencyFormats.en).format(value);
        }
        if (format === 'number') {
          return (numberFormats[L] || numberFormats.en).format(value);
        }
        if (format === 'date') {
          return (dateFormats[L] || dateFormats.en).format(value);
        }
        return value;
      }
    },

    detection: {
      order: ['localStorage', 'navigator'],
      caches: ['localStorage']
    },

    // Fold region variants (en-GB, en-US, fr-CA, ...) down to their base
    // language — only bare language-code folders exist under public/locales/.
    load: 'languageOnly',

    backend: {
      loadPath: '/locales/{{lng}}/{{ns}}.json'
    },

    react: {
      useSuspense: false
    }
  });

export default i18n;